import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import '../../style/components/blocks/user_friendly_error.css';

// User-friendly error notification component
export const UserFriendlyError = ({ 
  error, 
  onRetry = null, 
  onDismiss = null,
  showDetails = false,
  supportInfo = {
    phone: '(844) 204-2206',
    email: 'cyberprotection@aflac.com',
    hours: 'Monday - Friday: 7am - 7pm'
  }
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [showFullDetails, setShowFullDetails] = useState(false);

  useEffect(() => {
    // Auto-dismiss non-critical errors after 10 seconds
    if (error.severity !== 'critical') {
      const timer = setTimeout(() => {
        handleDismiss();
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) onDismiss();
  };

  const handleRetry = () => {
    if (onRetry) onRetry();
  };

  const getErrorIcon = () => {
    switch (error.severity) {
      case 'critical':
        return '⚠️';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      default:
        return 'ℹ️';
    }
  };

  const getErrorMessage = () => {
    // Return user-friendly messages based on error type
    const errorMap = {
      'network': 'We\'re having trouble connecting to our servers. Please check your internet connection.',
      'authentication': 'Your session has expired. Please log in again.',
      'authorization': 'You don\'t have permission to access this feature.',
      'validation': 'Please check the information you entered and try again.',
      'server': 'Our servers are experiencing issues. Please try again in a few minutes.',
      'timeout': 'The request took too long to complete. Please try again.',
      'not_found': 'The page or resource you\'re looking for couldn\'t be found.',
      'rate_limit': 'Too many requests. Please wait a moment before trying again.',
      'maintenance': 'We\'re currently performing maintenance. Please try again later.',
      'generic': error.userMessage || 'Something went wrong. Please try again or contact support if the problem persists.'
    };

    return errorMap[error.type] || errorMap.generic;
  };

  if (!isVisible) return null;

  return (
    <div className={`user-friendly-error user-friendly-error--${error.severity}`} role="alert">
      <div className="user-friendly-error__header">
        <span className="user-friendly-error__icon">{getErrorIcon()}</span>
        <h3 className="user-friendly-error__title">
          {error.severity === 'critical' ? 'Critical Error' : 
           error.severity === 'error' ? 'Error' : 
           error.severity === 'warning' ? 'Warning' : 'Notice'}
        </h3>
        {error.severity !== 'critical' && (
          <button 
            className="user-friendly-error__close" 
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            ×
          </button>
        )}
      </div>

      <div className="user-friendly-error__content">
        <p className="user-friendly-error__message">{getErrorMessage()}</p>

        {error.suggestions && error.suggestions.length > 0 && (
          <div className="user-friendly-error__suggestions">
            <p><strong>Try these steps:</strong></p>
            <ul>
              {error.suggestions.map((suggestion, index) => (
                <li key={index}>{suggestion}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="user-friendly-error__actions">
          {onRetry && (
            <button 
              className="button -blue user-friendly-error__retry" 
              onClick={handleRetry}
            >
              Try Again
            </button>
          )}
          
          {error.severity === 'critical' && (
            <div className="user-friendly-error__support">
              <p><strong>Need immediate help?</strong></p>
              <p>📞 <a href={`tel:${supportInfo.phone.replace(/[^\d]/g, '')}`}>{supportInfo.phone}</a></p>
              <p>✉️ <a href={`mailto:${supportInfo.email}`}>{supportInfo.email}</a></p>
              <p className="user-friendly-error__hours">{supportInfo.hours}</p>
            </div>
          )}
        </div>

        {showDetails && (
          <details className="user-friendly-error__details">
            <summary onClick={() => setShowFullDetails(!showFullDetails)}>
              Technical Details {showFullDetails ? '▼' : '▶'}
            </summary>
            {showFullDetails && (
              <div className="user-friendly-error__technical">
                <p><strong>Error ID:</strong> {error.id}</p>
                <p><strong>Time:</strong> {error.timestamp}</p>
                {error.code && <p><strong>Code:</strong> {error.code}</p>}
                {error.originalMessage && (
                  <p><strong>Technical Message:</strong> {error.originalMessage}</p>
                )}
                {error.context && Object.keys(error.context).length > 0 && (
                  <details>
                    <summary>Context Data</summary>
                    <pre>{JSON.stringify(error.context, null, 2)}</pre>
                  </details>
                )}
              </div>
            )}
          </details>
        )}
      </div>
    </div>
  );
};

// Toast notification for less severe errors
export const ErrorToast = ({ error, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleDismiss();
    }, 5000); // Auto-dismiss after 5 seconds

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) onDismiss();
  };

  if (!isVisible) return null;

  return (
    <div className={`error-toast error-toast--${error.severity}`}>
      <span className="error-toast__icon">
        {error.severity === 'error' ? '❌' : '⚠️'}
      </span>
      <span className="error-toast__message">{error.userMessage}</span>
      <button className="error-toast__close" onClick={handleDismiss}>×</button>
    </div>
  );
};

export default UserFriendlyError;
